import { createApiClient } from "../apiService";
import {
  addIndexationLogosToPublicationTypes,
  addIndexationLogoToPublicationTypes,
  dimensionsTypes,
  indexation_logoTypes,
  renameArticleTypesTypes,
} from "./dimensionTypes";
import { affiliationTypes } from "../affiliation/affiliationTypes";
import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { useToken } from "../tokenUtils";

const apiClient = createApiClient("");
const domain = getSubdomain();

const handleResponse = <T>(response: any): T => {
  return response.data;
};
const handlePostRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const getArticleTypes = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    `${domain}/article-types`,
  );
  return handleResponse(response);
};

const getAllArticleTypes = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    `${domain}/article-types-all`,
  );
  return handleResponse(response);
};

const deleteArticleTypes = async (name: string): Promise<void> => {
  const token = useToken();

  await apiClient.delete(`${domain}/article-types?name=${name}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const renameArticleTypes = async (
  renameArticleTypesRequest: renameArticleTypesTypes,
): Promise<void> => {
  const token = useToken();

  await apiClient.put(
    `dimension/article-types?name=${renameArticleTypesRequest.name}&new_name=${renameArticleTypesRequest.new_name}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
};

const addArticleTypes = async (name: string): Promise<void> => {
  const token = useToken();
  if (!token) {
    throw new Error("Faltan token");
  }

  const response = await handlePostRequest(
    `${domain}/article-types?name=${name}`,
    {},
    token,
  );
  return response;
};

const getArticleStatus = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_status",
  );
  return handleResponse(response);
};

const getReviewStatus = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/review_status",
  );
  return handleResponse(response);
};

const getArticleAuthorContributions = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_author_contribution_types",
  );
  return handleResponse(response);
};

const getPublicationReviewType = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/publication_review_type",
  );
  return handleResponse(response);
};

const getContributionList = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_author_contribution_types",
  );
  return handleResponse(response);
};

const getAffiliations = async (): Promise<affiliationTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await apiClient.get<affiliationTypes[]>("/affiliation/", {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return handleResponse(response);
};

const getIndexationLogo = async () => {
  const token = useToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await apiClient.get<indexation_logoTypes[]>(
    `indexation_logo/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return handleResponse(response);
};

const addIndexationLogoToPublication = async (
  addIndexationLogoToPublicationRequest: addIndexationLogoToPublicationTypes,
) => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await handlePostRequest(
    `indexation_logo/${addIndexationLogoToPublicationRequest.publication_slug}/add_indexation/${addIndexationLogoToPublicationRequest.id_indexation_logo}`,
    {},
    token,
  );
  return response;
};

const addIndexationLogosToPublication = async (
  addIndexationLogosToPublicationRequest: addIndexationLogosToPublicationTypes,
) => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const res = await apiClient.put(
    `indexation_logo/${addIndexationLogosToPublicationRequest.publication_slug}/add_indexations`,
    addIndexationLogosToPublicationRequest.id_indexation_logo_list,
    { headers },
  );

  return res.data;
};

const removeIndexationLogoFromPublication = async (
  removeIndexationLogoFromPublicationRequest: addIndexationLogoToPublicationTypes,
) => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await apiClient.delete(
    `indexation_logo/${removeIndexationLogoFromPublicationRequest.publication_slug}/remove_indexation/${removeIndexationLogoFromPublicationRequest.id_indexation_logo}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return handleResponse(response);
};

const removeIndexationLogosFromPublication = async (
  removeIndexationLogosFromPublicationRequest: addIndexationLogosToPublicationTypes,
) => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await apiClient.request({
    url: `indexation_logo/${removeIndexationLogosFromPublicationRequest.publication_slug}/remove_indexations`,
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
    data: removeIndexationLogosFromPublicationRequest.id_indexation_logo_list,
  });
  return handleResponse(response);
};

const getIndexationLogosByPublication = async (): Promise<
  indexation_logoTypes[]
> => {
  const response = await apiClient.get<indexation_logoTypes[]>(
    `indexation_logo/${domain}/logos`,
  );
  return handleResponse(response);
};

const dimensionsService = {
  getArticleTypes,
  getAllArticleTypes,
  deleteArticleTypes,
  renameArticleTypes,
  addArticleTypes,
  getArticleStatus,
  getReviewStatus,
  getArticleAuthorContributions,
  getPublicationReviewType,
  getContributionList,
  getAffiliations,
  getIndexationLogo,
  addIndexationLogoToPublication,
  addIndexationLogosToPublication,
  removeIndexationLogoFromPublication,
  removeIndexationLogosFromPublication,
  getIndexationLogosByPublication,
};

export default dimensionsService;
